import { Check, ArrowLeft, RotateCcw, History } from 'lucide-react';
import { useDayChanges, type DailyRosterEntry, type DayChangeDTO } from '../api/hooks.js';
import { decisionPill, hhmmFromMinutes } from '../lib/saisie.js';

// Success summary shown after a day is closed (validate-day/final → 200).
// Read-only — the ledger has already committed; this just confirms.
// Rendered INSIDE the Saisie page shell (which owns the date navigator and
// the day strip), so it carries no page wrapper of its own.

interface Props {
  date: string;
  entityId: string;
  rows: DailyRosterEntry[];
  validatedCount: number;
  validatedBy?: string | null;
  onBack: () => void;
  /** Provided only to holders of attendance.validate_final. */
  onReopen?: () => void;
  reopening?: boolean;
  customLabel?: (code: string) => string | undefined;
}

export function DayValidated({
  date, entityId, rows, validatedCount, validatedBy, onBack, onReopen, reopening, customLabel,
}: Props) {
  const arbitrated = rows.filter((r) => r.record?.decisionType != null);
  const totalMinutes = rows.reduce((acc, r) => acc + (r.recordStatus.durationMinutes ?? 0), 0);
  const changes = useDayChanges({ date, entityId });
  const edits = changes.data?.changes ?? [];

  return (
    <div className="sq-validated" data-testid="day-validated">
      <div className="sq-validated-badge">
        <Check size={48} aria-hidden="true" />
      </div>
      <div className="page-eyebrow">Saisie · {date}</div>
      <h1 className="page-title">Journée validée</h1>
      <p className="page-subtitle">
        {validatedCount} enregistrement{validatedCount > 1 ? 's' : ''} · {arbitrated.length} exception
        {arbitrated.length > 1 ? 's' : ''} arbitrée{arbitrated.length > 1 ? 's' : ''} · 0 en attente.
        {validatedBy ? ` Validée par ${validatedBy}.` : ''}
      </p>

      <div className="sq-stats">
        <div className="sq-stat">
          <div className="sq-stat-val">{rows.length}</div>
          <div className="sq-stat-lbl">Employés</div>
        </div>
        <div className="sq-stat">
          <div className="sq-stat-val" style={{ color: 'var(--ok)' }}>{arbitrated.length}</div>
          <div className="sq-stat-lbl">Arbitrées</div>
        </div>
        <div className="sq-stat">
          <div className="sq-stat-val">{hhmmFromMinutes(totalMinutes)}</div>
          <div className="sq-stat-lbl">Cumulées</div>
        </div>
      </div>

      {arbitrated.length > 0 && (
        <div className="sq-summary">
          {arbitrated.map((r) => {
            const pill = decisionPill(
              r.record?.decisionType ?? null,
              r.record?.decisionCustomCode ?? null,
              r.record?.status ?? null,
              customLabel,
            );
            return (
              <div className="sq-summary-row" key={r.employee.id}>
                <span className="sq-summary-name">{r.employee.name}</span>
                {pill && <span className={`sq-pill tone-${pill.tone}`}>{pill.label}</span>}
              </div>
            );
          })}
        </div>
      )}

      {/* What the reviewer changed after the saisie was signed. Whoever
          prepared the day sees it here, on the day itself — instead of
          discovering it in the payroll grid a month later. */}
      {edits.length > 0 && (
        <div className="sq-changes" data-testid="day-changes">
          <div className="sq-changes-h">
            <History size={14} aria-hidden="true" /> Modifications depuis la validation initiale
          </div>
          {edits.map((c) => (
            <div className="sq-changes-row" key={c.id}>
              <span className="sq-changes-who">{c.by ?? '—'}</span>
              <span className="sq-changes-what">{changeLabel(c)}</span>
              <span className="sq-changes-when">{fmtTime(c.at)}</span>
            </div>
          ))}
        </div>
      )}

      <div className="sq-validated-actions">
        <button type="button" className="btn btn-sm" onClick={onBack} data-testid="validated-back">
          <ArrowLeft aria-hidden="true" /> Revoir le tableau
        </button>
        {onReopen && (
          <button
            type="button"
            className="btn btn-sm"
            onClick={onReopen}
            disabled={reopening}
            data-testid="reopen-day"
          >
            <RotateCcw aria-hidden="true" /> Rouvrir la journée
          </button>
        )}
      </div>
    </div>
  );
}

// ── audit → one readable sentence ────────────────────────────
// The payloads already carry from→to diffs; this only has to name the
// three attendance actions a reviewer can produce on a closed day.
const DECISION_FR: Record<string, string> = {
  PRESENT: 'Présent', TT: 'Télétravail', CONGE: 'Congé', MALADIE: 'Maladie',
  RDV_PERSO: 'RDV perso', RECUP: 'Récup', AUTRE: 'Autre',
};
const decisionFr = (v: unknown): string => (v == null ? 'aucune' : DECISION_FR[String(v)] ?? String(v));

export function changeLabel(c: DayChangeDTO): string {
  const p = (c.payload ?? {}) as { from?: Record<string, unknown> | null; to?: Record<string, unknown> | null };
  const who = c.employeeName ? `${c.employeeName} — ` : '';
  switch (c.action) {
    case 'attendance.decision':
      return `${who}décision ${decisionFr(p.from?.decisionType)} → ${decisionFr(p.to?.decisionType)}`;
    case 'attendance.notified': {
      const fr = (v: unknown) => (v === 'YES' ? 'oui' : v === 'NO' ? 'non' : 'sans réponse');
      return `${who}« prévenu ? » ${fr(p.from?.notifiedInAdvance)} → ${fr(p.to?.notifiedInAdvance)}`;
    }
    case 'attendance.update': {
      const t = p.to ?? {};
      const f = p.from ?? {};
      const bits: string[] = [];
      if (f.checkIn !== t.checkIn) bits.push(`entrée ${f.checkIn ?? '—'} → ${t.checkIn ?? '—'}`);
      if (f.checkOut !== t.checkOut) bits.push(`sortie ${f.checkOut ?? '—'} → ${t.checkOut ?? '—'}`);
      if (f.status !== t.status) bits.push(`statut ${f.status ?? '—'} → ${t.status ?? '—'}`);
      return `${who}${bits.length ? bits.join(' · ') : 'pointage modifié'}`;
    }
    default:
      return `${who}${c.action}`;
  }
}

function fmtTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ''
    : d.toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
